/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, Flex, getColor, IconUserOutlined, Menu, useTheme } from "@powerledger/ui-component-lib";
import { kebabCase } from "lodash";
import { FC, Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";

import { useLogout } from "@/app/hooks";
import { privateRoutes } from "@/app/navigation/routes/private-routes";
import { PrivateRoutes } from "@/app/navigation/routes/routes.type";
import { LogoutSVG } from "@/assets/icons";

import { AccountStatus } from "../../types/generated/graphql";
import { LayoutHeader } from "./header/layout-header";
import { LayoutProps } from "./layout.types";
import { LayoutView } from "./layout-view";
import { LayoutSider } from "./sider";
import { TradingHaltNotifier } from "./trading-halt-notifier";
import { getIconComponents, useLayout } from "./use-layout";

const LogoutButton = ({ iconOnly = false }) => {
  const { logout } = useLogout();
  const { t } = useTranslation();
  return (
    <Flex
      key="logout"
      data-tip={t("Logout")}
      sx={{ justifyContent: "center" }}
      data-for="routes-title"
      data-testid="logout-menu"
    >
      <Menu.Item icon={<LogoutSVG />} handleOnClick={logout} iconOnly={iconOnly}>
        {t("Logout")}
      </Menu.Item>
    </Flex>
  );
};

export const Layout: FC<LayoutProps> = ({ children, sider, header, footer }) => {
  const { t } = useTranslation();
  const { theme }: { theme: any } = useTheme();
  const {
    activeRoutes,
    sidebarOpen,
    handleToggleRoute,
    updateSidebarState,
    userInfo,
    expandedRoutes,
    accountStatus,
    handleNavigate,
    getFilteredRoutes,
    hideRegistryDropdown,
  } = useLayout();

  const siderWidth = sidebarOpen ? theme.layouts.layoutSider.width : theme.layouts.layoutSider.collapsedWidth;
  const headerHeight = theme.layouts.header?.height;

  const renderSubRoutes = useCallback(
    (path: string, displayableSubRoutes: PrivateRoutes[]) => {
      return displayableSubRoutes.map(({ path: subPath, layout: subRouteLayout }) => {
        const testID = kebabCase(`${path}${subRouteLayout?.navTitle}-sub-menu`); //market-spot-trade-sub-menu
        return (
          <Box data-tip={t(subRouteLayout?.navTitle ?? "")} key={subPath} data-for="routes-title" data-testid={testID}>
            <Menu.Item
              isSubList
              sx={{
                ml: sidebarOpen ? 4 : 0,
                svg: {
                  scale: "0.75",
                },
              }}
              icon={
                getIconComponents()[subRouteLayout?.icon || ""] ??
                getIconComponents()[subRouteLayout?.navTitle || ""] ?? <IconUserOutlined size={3} />
              }
              handleOnClick={() => handleNavigate(path, subPath)}
              active={activeRoutes.some((route) => route.path === path + subPath)}
              iconOnly={!sidebarOpen}
            >
              {t(subRouteLayout?.navTitle || "")}
            </Menu.Item>
          </Box>
        );
      });
    },
    [activeRoutes, handleNavigate, sidebarOpen, t],
  );

  const sidebarRoutes = useMemo(() => {
    const routeJsx = getFilteredRoutes(privateRoutes).map(({ path, subRoutes, layout }) => {
      const { navTitle = "", icon = "" } = layout ?? {};
      const IconComponent = getIconComponents()[icon] ?? getIconComponents()[navTitle] ?? <IconUserOutlined size={5} />;
      const currentRouteActive = activeRoutes.some((route) => route.path === path);
      const displayableSubRoutes = getFilteredRoutes(subRoutes ?? []);
      return (
        <Fragment key={path}>
          <Box data-tip={t(layout?.navTitle || "")} data-for="routes-title" data-testid={`${kebabCase(navTitle)}-menu`}>
            <Menu.Item
              icon={IconComponent}
              handleOnClick={() => {
                if (displayableSubRoutes.length) {
                  handleToggleRoute(path);
                } else {
                  handleNavigate(path, "");
                }
              }}
              active={currentRouteActive}
              iconOnly={!sidebarOpen}
            >
              {t(navTitle)}
            </Menu.Item>
          </Box>
          {(expandedRoutes.includes(path) || currentRouteActive) && renderSubRoutes(path, displayableSubRoutes)}
        </Fragment>
      );
    });
    routeJsx.push(<LogoutButton key="logout" iconOnly={!sidebarOpen} />);
    return routeJsx;
  }, [
    getFilteredRoutes,
    activeRoutes,
    handleToggleRoute,
    handleNavigate,
    renderSubRoutes,
    expandedRoutes,
    sidebarOpen,
    t,
  ]);

  const layoutSider = useMemo(
    () => (
      <LayoutSider
        sider={sider}
        sidebarRoutes={sidebarRoutes}
        sidebarOpen={sidebarOpen}
        updateSidebarState={updateSidebarState}
      />
    ),
    [sidebarOpen, sidebarRoutes, sider, updateSidebarState],
  );

  const toolTipComponent = useMemo(
    () =>
      !sidebarOpen && (
        <ReactTooltip
          id="routes-title"
          backgroundColor={getColor(theme, "shadow")}
          getContent={(content) => <Box sx={{ color: "text" }}>{content}</Box>}
        />
      ),
    [sidebarOpen, theme],
  );

  const layoutHeader = useMemo(
    () => (
      <>
        <TradingHaltNotifier skipQuery={accountStatus !== AccountStatus.Active} />
        <LayoutHeader header={header} userInfo={userInfo} hideRegistryDropdown={hideRegistryDropdown} />
      </>
    ),
    [accountStatus, header, userInfo, hideRegistryDropdown],
  );

  return (
    <LayoutView
      siderWidth={siderWidth}
      headerHeight={headerHeight}
      tooltipComponent={toolTipComponent}
      footer={footer}
      header={layoutHeader}
      sider={layoutSider}
    >
      {children}
    </LayoutView>
  );
};
